import type { ReactNode } from 'react';
import { formatVnd, GAME_BY_ID } from '@ruby-cms/shared';
import type { Command } from '../pages/Commands';
import { describeHu } from '../pages/JackpotConfig';
import { LodeLimitChange, LodeRateChange } from './LodeChange';

type Obj = Record<string, any>;

const gameName = (id: string) => GAME_BY_ID.get(id)?.name ?? id;
const tableName = (gid: string) => gameName(gid === '5996' ? 'kts_5996' : 'ktrng_' + gid);
const pct = (x: unknown) => (typeof x === 'number' ? Math.round(x * 1000) / 10 + '%' : '-');
const vnd = (x: unknown, zero: string) => (typeof x === 'number' && x > 0 ? <span className="num">{formatVnd(x)}</span> : zero);
const onOff = (x: unknown) => (x === false ? 'Tắt' : 'Bật');

function ControlChange({ gid, b }: { gid: string; b: Obj }) {
  return (
    <dl className="facts">
      <dt>Bàn</dt><dd>{tableName(gid)} <span className="num">{gid}</span></dd>
      <dt>Điều khiển qua quỹ</dt><dd>{b.bat ? 'Bật' : 'Tắt, kết quả ngẫu nhiên'}</dd>
      <dt>Phần cược vào quỹ</dt><dd className="num">{pct(b.quy?.tyLeVaoQuy)}</dd>
      <dt>Trần quỹ</dt><dd>{vnd(b.quy?.quyToiDa, 'Không trần')}</dd>
      <dt>Trả tối đa mỗi ván</dt><dd>{vnd(b.quy?.traToiDaMoiVan, 'Chỉ giới hạn bởi quỹ')}</dd>
      <dt>Số bot</dt><dd className="num">{b.bot?.soBot ?? '-'}</dd>
      <dt>Số bot chat</dt><dd className="num">{b.bot?.soBotChat ?? '-'}</dd>
      <dt>Bot đặt cược</dt><dd>{onOff(b.bot?.cuoc?.bat)}</dd>
      <dt>Tỷ lệ bot vào ván</dt><dd className="num">{pct(b.bot?.cuoc?.tyLeVaoVan)}</dd>
    </dl>
  );
}

function GameBotChange({ gid, b }: { gid: string; b: Obj }) {
  return (
    <dl className="facts">
      <dt>Game</dt><dd>{gameName(gid)} <span className="num">{gid}</span></dd>
      <dt>Bot của game</dt><dd>{b.bot?.bat === false ? 'Tắt hết bot (cược và chat)' : 'Bật'}</dd>
      <dt>Số bot chat</dt><dd className="num">{b.bot?.soBotChat ?? '-'}</dd>
      <dt>Bot đặt cược</dt><dd>{onOff(b.bot?.cuoc?.bat)}</dd>
      <dt>Ban đêm bot cược thưa hơn</dt><dd className="num">{typeof b.bot?.cuoc?.heSoDem === 'number' ? '× ' + b.bot.cuoc.heSoDem : '-'}</dd>
    </dl>
  );
}

const secs = (x: unknown) => (typeof x === 'number' ? <span className="num">{x} giây</span> : '-');

function GlobalChange({ g }: { g: Obj }) {
  const bn = g.hu?.botNo ?? {};
  const bg = g.hu?.botGop;
  const tl = g.thongBao?.thangLon ?? {};
  return (
    <dl className="facts">
      <dt>Người thật tự nổ hũ</dt>
      <dd>{g.hu?.nguoiThatNoTuNhien === true ? 'Bật: người thật nổ hũ theo luật game' : 'Tắt: chỉ bot nổ hũ; người thật chỉ nổ khi founder chỉ định'}</dd>
      <dt>Bot nổ hũ</dt><dd>{onOff(bn.bat)}</dd>
      <dt>Xác suất bot nổ mỗi lần kiểm</dt><dd className="num">{pct(bn.tyLe)}</dd>
      <dt>Kiểm mỗi</dt><dd>{secs(bn.giayKiem)}</dd>
      <dt>Mỗi hũ bot nổ lại sau</dt><dd>{typeof bn.gioToiThieu === 'number' || typeof bn.gioToiDa === 'number' ? <span className="num">{bn.gioToiThieu ?? 0} đến {bn.gioToiDa ?? 0} giờ</span> : '-'}</dd>
      <dt>Bot chỉ nổ khi hũ ≥ khởi tạo ×</dt><dd className="num">{bn.heSoNguong ?? '-'}</dd>
      {bg && (
        <>
          <dt>Bot góp hũ</dt><dd>{onOff(bg.bat)}</dd>
          <dt>Hũ tăng mỗi phút</dt><dd className="num">{typeof bg.phanTramMoiPhut === 'number' ? bg.phanTramMoiPhut + ' % tiền khởi tạo' : '-'}</dd>
          <dt>Hũ tăng tối đa tới khởi tạo ×</dt><dd className="num">{bg.heSoTran ?? '-'}</dd>
        </>
      )}
      <dt>Chữ chạy sảnh</dt><dd>{onOff(g.thongBao?.bat)}</dd>
      <dt>Báo nổ hũ</dt><dd>{onOff(g.thongBao?.noHu?.bat)}</dd>
      <dt>Báo thắng lớn ảo của bot</dt><dd>{onOff(tl.bat)}</dd>
      <dt>Cách nhau</dt><dd>{secs(tl.giayMin)} đến {secs(tl.giayMax)}</dd>
      <dt>Tiền thắng ảo</dt><dd>{vnd(tl.tienMin, '0')} đến {vnd(tl.tienMax, '0')}</dd>
      <dt>Bot chơi ảo cho bảng xếp hạng</dt><dd>{onOff(g.bot?.choiAo?.bat)}</dd>
      <dt>Mỗi game một ván ảo mỗi</dt><dd>{secs(g.bot?.choiAo?.giay)}</dd>
    </dl>
  );
}

function LimitChange({ doc }: { doc: Obj }) {
  const rows = Object.entries(doc);
  if (!rows.length) return <p className="muted">Không còn hạn mức riêng nào; mọi game dùng mặc định.</p>;
  return (
    <dl className="facts">
      {rows.map(([id, g]) => (
        <div key={id} style={{ display: 'contents' }}>
          <dt>{gameName(id)}</dt>
          <dd>{g.bat === false ? 'Tạm dừng nhận cược' : <>Từ {vnd(g.cuocMin, 'mức mặc định')} đến {vnd(g.cuocMax, 'không giới hạn')}</>}</dd>
        </div>
      ))}
    </dl>
  );
}

// Ban Ca (config `ban_ca`): khoa khong co = game dung mac dinh
function FishChange({ doc }: { doc: Obj }) {
  const room = (id: string, name: string) => {
    const r = doc.phong?.[id] ?? {};
    return (
      <div style={{ display: 'contents' }}>
        <dt>{name}</dt>
        <dd>Trả {typeof r.rtp === 'number' ? <span className="num">{pct(r.rtp)}</span> : 'mặc định'}, mức đạn {Array.isArray(r.mucCuoc) ? <span className="num">{r.mucCuoc.map((x: number) => formatVnd(x)).join(', ')}</span> : 'mặc định'}</dd>
      </div>
    );
  };
  const h = doc.hu ?? {};
  const b = doc.bot ?? {};
  const def = (x: unknown, f: (v: any) => ReactNode) => (x == null ? 'mặc định' : f(x));
  return (
    <dl className="facts">
      {room('1', 'Săn Cá Biển Sâu')}
      {room('3', 'Hải Vực Cự Thú')}
      <dt>Góp vào hũ mỗi viên</dt><dd>{def(h.phanTramVaoHu, (v) => <span className="num">{v}%</span>)}</dd>
      <dt>Hũ khởi tạo</dt><dd>{def(h.huKhoiTao, (v) => <span className="num">{formatVnd(v)}</span>)}</dd>
      <dt>Hệ số nổ hũ</dt><dd>{def(h.heSoNo, (v) => <span className="num">{v}</span>)}</dd>
      <dt>Bot</dt><dd>{def(b.bat, (v) => (v ? 'Bật' : 'Tắt'))}</dd>
      <dt>Số bot mỗi bàn</dt><dd>{def(b.soBot, (v) => <span className="num">{v[0]} đến {v[1]}</span>)}</dd>
    </dl>
  );
}

function RtpChange({ doc, focus }: { doc: Obj; focus: string | null }) {
  const rows = Object.entries(doc).filter(([, g]) => typeof g?.rtpMucTieu === 'number');
  return (
    <div className="stack">
      {focus ? <p>Game đổi: <strong>{gameName(focus)}</strong>, RTP mục tiêu {typeof doc[focus]?.rtpMucTieu === 'number' ? <span className="num">{doc[focus].rtpMucTieu}%</span> : 'về mặc định'}.</p> : null}
      {rows.length ? (
        <dl className="facts">
          {rows.map(([id, g]) => (
            <div key={id} style={{ display: 'contents' }}><dt>{gameName(id)}</dt><dd className="num">{g.rtpMucTieu}%</dd></div>
          ))}
        </dl>
      ) : <p className="muted">Sau lệnh này không game nào đặt RTP mục tiêu; mọi game dùng mặc định.</p>}
    </div>
  );
}

// Nhip Tai Xiu (config `game`, khoa nhip): each game's own clock, a missing key keeps the file default
function NhipChange({ doc, focus }: { doc: Obj; focus: string | null }) {
  const rows = Object.entries(doc).filter(([, g]) => g?.nhip && typeof g.nhip === 'object');
  const secs = (x: unknown) => (typeof x === 'number' ? x + ' giây' : 'mặc định');
  return (
    <div className="stack">
      {focus ? <p>Game đổi: <strong>{gameName(focus)}</strong>{doc[focus]?.nhip ? '' : ', về nhịp mặc định'}. Áp dụng từ ván kế tiếp.</p> : null}
      {rows.length ? (
        <dl className="facts">
          {rows.map(([id, g]) => (
            <div key={id} style={{ display: 'contents' }}>
              <dt>{gameName(id)}</dt>
              <dd className="num">cược {secs(g.nhip.giayDatCuoc)}, trả thưởng {secs(g.nhip.giayTraThuong)}, lệch {secs(g.nhip.lechGiay)}</dd>
            </div>
          ))}
        </dl>
      ) : <p className="muted">Sau lệnh này không bàn nào đặt nhịp riêng; mọi bàn dùng mặc định.</p>}
    </div>
  );
}

function IdList({ ids, empty }: { ids: string[]; empty: string }) {
  if (!ids.length) return <p className="muted">{empty}</p>;
  return <p>{ids.map(gameName).join(', ')}</p>;
}

/** What a CONFIG command writes on he A, readable for the checker. */
export function ConfigChange({ c }: { c: Command }) {
  const ten = String(c.payload.ten);
  const doc = (c.payload.noi_dung ?? {}) as Obj;
  if (ten === 'dieu_khien' && c.sourceType === 'games.control.global' && doc.global) return <GlobalChange g={doc.global} />;
  if (ten === 'dieu_khien' && c.sourceType === 'games.control.game' && c.sourceId && doc.game?.[c.sourceId]) return <GameBotChange gid={c.sourceId} b={doc.game[c.sourceId]} />;
  if (ten === 'dieu_khien' && c.sourceId && doc.game?.[c.sourceId]) return <ControlChange gid={c.sourceId} b={doc.game[c.sourceId]} />;
  if (ten === 'game' && c.sourceType === 'games.rtp') return <RtpChange doc={doc} focus={c.sourceId} />;
  if (ten === 'game' && c.sourceType === 'games.nhip') return <NhipChange doc={doc} focus={c.sourceId} />;
  if (ten === 'game') return <LimitChange doc={doc} />;
  if (ten === 'ban_ca') return <FishChange doc={doc} />;
  if (ten === 'hu') return <ul className="list">{describeHu(doc).map((x) => <li key={x}>{x}</li>)}</ul>;
  if (ten === 'lode_ty_le') return <LodeRateChange doc={doc} />;
  if (ten === 'lode_gioi_han') return <LodeLimitChange doc={doc} />;
  if (ten === 'an_game') return <IdList ids={Array.isArray(doc.ids) ? doc.ids : []} empty="Không ẩn game nào." />;
  if (ten === 'bao_tri') return <IdList ids={Array.isArray(doc.ids) ? doc.ids : []} empty="Không game nào bảo trì." />;
  return <pre className="pre">{JSON.stringify(doc, null, 2)}</pre>;
}
